import { constraintConfig } from '../../../constraintConfig'
import { getMergeAttachmentUrl, getReceiptNumber } from './MergeSelectedLineItemHeader'

export type MergeV2ModeResolved = 'bank' | 'ss'

export const isBankSource = (item: any): boolean => {
  if (!item) return false
  if (item._mergeSource === 'bank') return true
  const sourceId = Number(
    item.recordSourceId ?? item.recordSourceID ?? item.lineItemRecordSourceId ?? 0
  )
  return sourceId === constraintConfig.recordSourceId.BankFeed
}

export const hasAttachment = (item: any): boolean => !!getMergeAttachmentUrl(item)

const commentText = (d: any) => (d?.internalNotes?.trim() ? String(d.internalNotes).trim() : '')

const itemDetailText = (d: any) => (d?.itemDetail?.trim() ? String(d.itemDetail).trim() : '')

const itemSourceLabel = (recordSourceId?: number): string => {
  if (recordSourceId === constraintConfig.recordSourceId.BankFeed) return 'Bank'
  if (recordSourceId === constraintConfig.recordSourceId.WebApp) return 'Web'
  if (recordSourceId === constraintConfig.recordSourceId.Mobile) return 'Mobile'
  if (recordSourceId === constraintConfig.recordSourceId.GorillaExpense) return 'Gorilla'
  return recordSourceId != null ? `SourceId=${recordSourceId}` : '—'
}

const formatItemDate = (itemDate: any): string => {
  if (!itemDate) return '—'
  return String(itemDate).split('T')[0] || '—'
}

const formatAmount = (amount: any): string => {
  const n = Number(String(amount ?? '').replace(/[^0-9.-]/g, ''))
  return Number.isFinite(n) ? n.toFixed(2) : '—'
}

const mergeFromMeta = (fromItem: any): string => {
  const fromId = fromItem?.expenseItemId ?? '—'
  const itemDate = formatItemDate(fromItem?.itemDate)
  const amount = formatAmount(fromItem?.amount)
  const paymentMethod = fromItem?.paymentMethod?.title || '—'
  const itemSource = itemSourceLabel(fromItem?.recordSourceId)
  return `SS LineItemID: ${fromId}, ItemDate: ${itemDate}, Amount: ${amount}, PaymentMethod: ${paymentMethod}, ItemSource: ${itemSource}`
}

/** InternalNotes (UI: Internal Notes) — merge metadata from From line item. */
export const buildMergeInternalNotes = (fromItem: any, intoNotes = ''): string => {
  const mergeBlock = `This Expense Item Merged by user from ${mergeFromMeta(fromItem)}, by user using merge v1 / v2 feature from Mobile or WebApp.`
  const existing = intoNotes?.trim() || ''
  return existing ? `${existing} | ${mergeBlock}` : mergeBlock
}

/** @deprecated use buildMergeInternalNotes */
export const buildMergeItemDetailNotes = buildMergeInternalNotes

/** Merge suffix appended to Item Description after user text. */
export const buildMergeItemDescriptionSuffix = (fromItem: any): string => {
  return `This Expense Item Merged by user from ${mergeFromMeta(fromItem)}.`
}

/** Existing Into (or From) Item Description only — for the editable textarea. */
export const getExistingItemDescription = (fromItem: any, intoItem: any): string => {
  return itemDetailText(intoItem) || itemDetailText(fromItem) || ''
}

/**
 * Final Item Description = user/existing text + postfilled merge suffix.
 * Avoids duplicating the suffix if the user text already ends with it.
 */
export const buildFinalItemDescription = (userOrExistingText: string, fromItem: any): string => {
  const suffix = buildMergeItemDescriptionSuffix(fromItem)
  const base = (userOrExistingText || '').trim()
  if (!base) return suffix
  if (base.includes(suffix) || base.endsWith(suffix)) return base
  return `${base} ${suffix}`
}

/** ItemDetail (UI: Item Description) — existing description + merge suffix. */
export const buildMergeItemDescription = (fromItem: any, intoItem: any): string => {
  return buildFinalItemDescription(getExistingItemDescription(fromItem, intoItem), fromItem)
}

/**
 * Resolve From / Into for merge.
 * Bank (RecordSourceID = 4) is always Into (kept). Non-bank is From.
 * When both are non-bank, opening item is From and selected target is Into (unless swapped).
 */
export const resolveMergePair = (
  openingItem: any,
  targetItem: any,
  swapped = false
): { fromItem: any; intoItem: any; mergeMode: MergeV2ModeResolved; canSwap: boolean } => {
  if (!openingItem || !targetItem) {
    return {
      fromItem: openingItem,
      intoItem: targetItem,
      mergeMode: 'ss',
      canSwap: false,
    }
  }

  const openingIsBank = isBankSource(openingItem)
  const targetIsBank = isBankSource(targetItem)

  if (openingIsBank || targetIsBank) {
    const intoItem = openingIsBank ? openingItem : targetItem
    const fromItem = openingIsBank ? targetItem : openingItem
    return {
      fromItem,
      intoItem,
      mergeMode: 'bank',
      canSwap: false,
    }
  }

  if (swapped) {
    return {
      fromItem: targetItem,
      intoItem: openingItem,
      mergeMode: 'ss',
      canSwap: true,
    }
  }

  return {
    fromItem: openingItem,
    intoItem: targetItem,
    mergeMode: 'ss',
    canSwap: true,
  }
}

export type MergeFinalPreview = {
  receiptNo: string
  expenseItemId: string | number
  itemDate: string
  amount: string | number
  vendor: string
  paymentMethod: string
  category: string
  expenseType: string
  attachmentLabel: string
  /** Final ItemDetail (Item Description) */
  itemDetail: string
  /** Final InternalNotes (Item Detail) */
  comments: string
  changed: {
    receiptNo: boolean
    attachment: boolean
    itemDetail: boolean
    comments: boolean
  }
}

/**
 * Client-side preview of the kept (Into) line item after merge, aligned with:
 * - sp_Admin_OrgAdmin_MergeLineItems / MergeLineItemsV2 (SS → Bank)
 * - sp_Admin_OrgAdmin_MergeSSLineItemsV2 (SS → SS)
 */
export const buildFinalMergePreview = (
  fromItem: any,
  intoItem: any,
  mergeMode: MergeV2ModeResolved
): MergeFinalPreview => {
  const intoReceipt = getReceiptNumber(intoItem)
  const fromReceipt = getReceiptNumber(fromItem)
  const intoHasAtt = hasAttachment(intoItem)
  const fromHasAtt = hasAttachment(fromItem)

  let finalAttachment = intoHasAtt
  let finalReceiptNo = intoReceipt
  let attachmentMoved = false

  if (mergeMode === 'bank') {
    if (fromHasAtt) {
      finalAttachment = true
      finalReceiptNo = fromReceipt !== '—' ? fromReceipt : intoReceipt
      attachmentMoved = true
    }
  } else {
    if (!intoHasAtt && fromHasAtt) {
      finalAttachment = true
      finalReceiptNo = fromReceipt
      attachmentMoved = true
    }
  }

  const finalDetail = buildFinalItemDescription(getExistingItemDescription(fromItem, intoItem), fromItem)
  const finalComments = buildMergeInternalNotes(fromItem, commentText(intoItem))

  return {
    receiptNo: finalReceiptNo,
    expenseItemId: intoItem?.expenseItemId ?? '—',
    itemDate: formatItemDate(intoItem?.itemDate),
    amount: intoItem?.amount ?? '—',
    vendor: intoItem?.vendor?.name || '—',
    paymentMethod: intoItem?.paymentMethod?.title || '—',
    category: intoItem?.expenseCategory?.title || '—',
    expenseType: intoItem?.expenseType?.title || intoItem?.expenseTypeTitle || '—',
    attachmentLabel: finalAttachment ? 'Has receipt' : 'No receipt',
    itemDetail: finalDetail,
    comments: finalComments,
    changed: {
      receiptNo: finalReceiptNo !== intoReceipt,
      attachment: attachmentMoved,
      itemDetail: true,
      comments: true,
    },
  }
}

/**
 * Synthetic line-item object for MergeSelectedLineItemHeader “Merge Preview” panel
 * (same shape as From/To, reflecting SP merge outcome).
 */
export const buildMergedLineItemForPreview = (
  fromItem: any,
  intoItem: any,
  mergeMode: MergeV2ModeResolved,
  /** Optional live Item Description from the confirm textarea (before suffix). */
  userItemDescription?: string
): any => {
  if (!intoItem) return null
  const preview = buildFinalMergePreview(fromItem, intoItem, mergeMode)
  const fromAtt = getMergeAttachmentUrl(fromItem)
  const intoAtt = getMergeAttachmentUrl(intoItem)
  const useFromAttachment =
    mergeMode === 'bank'
      ? !!fromAtt
      : !intoAtt && !!fromAtt

  const fromDraftId =
    fromItem?.invoiceDataDraftId && Number(fromItem.invoiceDataDraftId) > 0
      ? fromItem.invoiceDataDraftId
      : null
  const intoDraftId =
    intoItem?.invoiceDataDraftId && Number(intoItem.invoiceDataDraftId) > 0
      ? intoItem.invoiceDataDraftId
      : null
  const finalDraftId =
    mergeMode === 'bank' ? fromDraftId || intoDraftId : fromDraftId || intoDraftId

  const receiptSource = useFromAttachment ? fromItem : intoItem
  const receiptNoRaw =
    preview.receiptNo !== '—'
      ? preview.receiptNo
      : getReceiptNumber(receiptSource)

  const descriptionBase =
    userItemDescription != null
      ? userItemDescription
      : getExistingItemDescription(fromItem, intoItem)
  const finalItemDescription = buildFinalItemDescription(descriptionBase, fromItem)

  return {
    ...intoItem,
    expenseItemId: intoItem.expenseItemId,
    itemDate: intoItem.itemDate,
    amount: intoItem.amount,
    vendor: intoItem.vendor,
    paymentMethod: intoItem.paymentMethod,
    expenseCategory: intoItem.expenseCategory,
    expenseType: intoItem.expenseType,
    expenseTypeTitle: intoItem.expenseTypeTitle,
    recordSourceId: intoItem.recordSourceId,
    createdDate: intoItem.createdDate || intoItem.createdDT,
    receiptUploadedDateTime:
      useFromAttachment
        ? fromItem?.receiptUploadedDateTime ||
          fromItem?.receiptUploadedDate ||
          fromItem?.invoiceDataDraft?.createdDT ||
          intoItem?.receiptUploadedDateTime
        : intoItem?.receiptUploadedDateTime || intoItem?.receiptUploadedDate,
    attachmentFile: useFromAttachment ? fromAtt : intoAtt,
    fileURL: useFromAttachment ? fromAtt : intoAtt,
    invoiceDataDraftId: finalDraftId,
    invoiceDataDraft: useFromAttachment
      ? fromItem?.invoiceDataDraft || intoItem?.invoiceDataDraft
      : intoItem?.invoiceDataDraft || fromItem?.invoiceDataDraft,
    invoiceNo:
      receiptNoRaw.startsWith('Draft #') || receiptNoRaw.startsWith('LI #') ? undefined : receiptNoRaw,
    itemDetail: finalItemDescription,
    internalNotes: preview.comments || intoItem?.internalNotes || '',
    _isMergePreview: true,
  }
}
